import React, {forwardRef, useState, useCallback} from 'react'
import styled, {css} from 'styled-components'
import {AkChevronDownIcon} from '@fuegokit/fuegoicons-react'
import RuleItemInnerVisualSlot from '@fuegokit/react/experimental/RuleBuilder/_RuleItemInnerVisualSlot'
import RuleItemUserActions from '@fuegokit/react/experimental/RuleBuilder/RuleItemUserActions'
import {
  Collapse,
  Box,
  BoxProps,
  Text,
  useBoolean,
  Button,
  ButtonProps,
  sx,
  SxProp,
  themeGet,
  useRefObjectAsForwardedRef,
  useOutsideClick,
  VisuallyHidden
} from '../..'
import {generateUniqueKey} from './utils'
import {ForwardRefComponent as PolymorphicForwardRefComponent} from '../../utils/polymorphic'

import {RuleItemProps, Spacing} from './types'

// Toggle Button
// ------------------------------------------------
const StyledToggleButton = styled(Button)<ToggleButtonProps & SxProp>`
  // reset
  appearance: none;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font: inherit;
  user-select: none;
  display: flex;
  align-items: center;
  color: inherit;
  //
  font-weight: normal;
  font-size: ${themeGet('fontSizes.2')};
  text-decoration: none;
  opacity: 0.85;
  font-weight: ${themeGet('fontWeights.semibold')};
  padding: ${themeGet('space.1')} ${themeGet('space.1')};
  border-radius: ${themeGet('radii.1')};
  &:hover,
  &:focus {
    opacity: 1;
    text-decoration: none;
    color: ${p => (p.isRuleItemDisabled ? themeGet('colors.text.disabled') : themeGet('colors.link.default'))};
    background-color: ${p =>
      p.isRuleItemDisabled
        ? themeGet('colors.background.neutral.subtle.default')
        : themeGet('colors.background.neutral.subtle.hovered')};
    cursor: ${p => (p.isRuleItemDisabled ? 'not-allowed' : '')};
  }
  &:active {
    opacity: 1;
    text-decoration: none;
    background-color: ${themeGet('colors.background.selected.default.[default]')};
    color: ${themeGet('colors.text.selected')};
  }
  &:focus,
  .focus-visible {
    outline: none;
    transition-duration: 0s, 0.2s;
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
  &:focus:not(:focus-visible):not(.focus-visible) {
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
  svg {
    transition: transform 0.15s ease;
  }
  &[data-state='open'] {
    --icon-rotation: 180deg;
    background-color: ${themeGet('colors.background.selected.default.[default]')};
    color: ${themeGet('colors.text.selected')};
    svg {
      transform: rotate(var(--icon-rotation));
    }
  }
  ${sx}
`
export interface ToggleButtonProps extends ButtonProps {
  children?: React.ReactNode
  /**
   * Event handler called when the open state of the collapsible changes.
   */
  onOpenChange?: (open: boolean) => void
  /**
   *
   */
  isRuleItemDisabled?: boolean
}

export const ToggleButton = ({children, onOpenChange, isRuleItemDisabled, open, ...props}: ToggleButtonProps) => {
  return (
    <StyledToggleButton data-state={open ? 'open' : ''} isRuleItemDisabled={isRuleItemDisabled} {...props}>
      {children}
    </StyledToggleButton>
  )
}

export interface CollapsibleRuleItemProps extends RuleItemProps {
  /**
   * Content to to render in the expanded and collapsed content.
   */
  children?: React.ReactNode
  /**
   * Event handler called when the open state of the collapsible changes.
   */
  onOpenChange?: (open: boolean) => void
  /**
   * Title of the rule item
   */
  title?: string
  /**
   * Description of the rule item
   */
  description?: string
  /**
   * Change the style whether the Rule Item is collapsed
   */
  isCollapsed?: boolean
  /**
   * Event handler called when the user selects a rule item (via mouse or keyboard).
   */
  onSelect?: (event: React.MouseEvent) => void
}

const spacingMap = {
  none: '',
  default: '6px',
  compact: themeGet('space.0'),
  relaxed: themeGet('space.3')
}
const borderStylesMap = {
  default: '1px solid',
  selected: '1px solid',
  hovered: '1px solid' // Add this line for the hover case
}
const borderColorMap = {
  default: (p: {isSelected?: boolean; disabled?: boolean}) =>
    // eslint-disable-next-line no-nested-ternary
    p.isSelected ? themeGet('colors.border.selected') : p.disabled ? 'transparent' : themeGet('colors.border.default'),
  hover: (p: {isSelected?: boolean; disabled?: boolean}) =>
    // eslint-disable-next-line no-nested-ternary
    p.isSelected
      ? themeGet('colors.border.selected')
      : p.disabled === true
      ? 'transparent'
      : themeGet('colors.border.default')
}

const Wrapper = styled(Box)<BoxProps & CollapsibleRuleItemProps & SxProp>`
  border-radius: ${themeGet('radii.2')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  justify-items: center;
  gap: ${themeGet('space.4')};

  border: 1px solid;
  border-color: ${themeGet('colors.border.default')};
  transition: background-color 0.1s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38);
  // we slow the transition in cases where the rule item is selected so that box-shadow doesn't render immediately;
  transition: ${p => (p.isSelected ? 'revert' : '350ms')};
  border-color: ${p => (p.isSelected ? themeGet('colors.border.selected') : 'transparent')};

  background-color: ${p =>
    p.disabled ? themeGet('colors.background.disabled') : themeGet('colors.background.neutral.default.[default]')};
  color: ${p => (p.disabled ? themeGet('colors.text.disabled') : themeGet('colors.text.default'))};
  user-select: none;

  // dynamic spacing
  padding-top: ${p => spacingMap[p.spacing as Spacing]};
  padding-bottom: ${p => spacingMap[p.spacing as Spacing]};
  padding-left: ${p => (p.spacing === 'none' ? '' : themeGet('space.3'))};
  padding-right: ${p => (p.spacing === 'none' ? '' : themeGet('space.3'))};

  &:hover {
    background-color: ${p =>
      p.disabled ? themeGet('colors.background.disabled') : themeGet('colors.background.neutral.subtle.default')};
    border: ${borderStylesMap.hovered}; // Use the updated map here
    border-color: ${p => borderColorMap.hover(p)};
    cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  }
  ${p =>
    p.isSelected &&
    css`
      &:hover {
        box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
        transition: background-color 0.1s ease-out, box-shadow 0.45s cubic-bezier(0.47, 0.03, 0.49, 1.38);
      }
    `}
  &:focus {
    border-color: ${themeGet('colors.border.focused')};
  }
  &:active {
    background-color: ${themeGet('colors.background.neutral.subtle.default')};
    border: 1px solid ${themeGet('colors.border.default')};
  }
  ${p =>
    p.disabled &&
    css`
      background-color: ${themeGet('colors.background.disabled')};
      color: ${themeGet('colors.text.disabled')};
      svg: {
        fill: ${themeGet('colors.icon.disabled')};
      }
    `}
  .rule-item-visual {
    svg,
    rect {
      fill: ${p => (p.disabled ? themeGet('colors.icon.disabled') : '')};
    }
  }
  ${sx}
`

// Rule item right title and description
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RuleItemRight = ({title = 'What', description = 'Description of what'}: any): JSX.Element => {
  return (
    <Box sx={{display: 'flex', flexDirection: 'column'}}>
      <Text as="p" sx={{fontWeight: 'semibold'}}>
        {title}
      </Text>
      <Text as="p">{description}</Text>
    </Box>
  )
}

/**
 * Rule item with collapsible content passed in as children. Provide a title and description.
 */
const CollapsibleRuleItem = forwardRef(({title, description, children, onOpenChange, ...props}, forwardedRef) => {
  const {
    loading,
    counter,
    spacing = 'default',
    loaderPosition = 'auto',
    leadingVisual: LeadingVisual,
    trailingVisual: TrailingVisual,
    trailingAction,
    isSelected: isSelectedProp,
    disabled: disabledProp,
    sx: sxProp,
    onSelect,
    as = 'div'
  } = props

  const [open, {toggle}] = useBoolean()
  const containerRef = React.useRef<HTMLDivElement>(null)
  useRefObjectAsForwardedRef(forwardedRef, containerRef)
  const showLeadingLoadingIndicator =
    loading && (loaderPosition === 'leading' || Boolean(LeadingVisual && loaderPosition !== 'trailing'))
  const showTrailingLoadingIndicator =
    loading && (loaderPosition === 'trailing' || Boolean(loaderPosition === 'auto' && !LeadingVisual))

  const [isActive, setIsActive] = useState(isSelectedProp)
  const isRuleItemSelected = isSelectedProp || isActive

  const [isDisabled, setIsDisabled] = useState(disabledProp)
  const isRuleItemDisabled = disabledProp || isDisabled

  const handleRuleItemSelect = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (isSelectedProp) return

      if (onSelect) {
        // onSelect(event, event.clientY)
        onSelect(event)
      }
    },
    [isSelectedProp, onSelect]
  )

  function handleRuleItemBlur() {
    setIsActive(false)
  }

  useOutsideClick({onClickOutside: handleRuleItemBlur, containerRef})

  return (
    <Wrapper
      as={as}
      ref={containerRef}
      isSelected={isRuleItemSelected}
      onClick={handleRuleItemSelect}
      disabled={isRuleItemDisabled}
      sx={sxProp}
      spacing={spacing}
    >
      <Box sx={{display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center', width: '100%'}}>
        <RuleItemInnerVisualSlot
          visualPosition="leading"
          showLoadingIndicator={showLeadingLoadingIndicator}
          hasLoadingIndicator={typeof loading === 'boolean'}
        >
          {typeof LeadingVisual === 'function' ? <LeadingVisual /> : LeadingVisual}
        </RuleItemInnerVisualSlot>

        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
          <RuleItemRight title={title} description={description} />

          <RuleItemInnerVisualSlot
            visualPosition="trailing"
            showLoadingIndicator={showTrailingLoadingIndicator}
            hasLoadingIndicator={typeof loading === 'boolean'}
          >
            {typeof TrailingVisual === 'function' ? <TrailingVisual /> : TrailingVisual}

            {/** @TODO: onOpenChange should be passed down to the toggle or dismiss buttons */}
            <ToggleButton
              disabled={isRuleItemDisabled}
              isRuleItemDisabled={isRuleItemDisabled}
              onClick={toggle}
              open={open}
            >
              <VisuallyHidden>Open</VisuallyHidden>
              <AkChevronDownIcon />
            </ToggleButton>
          </RuleItemInnerVisualSlot>
        </Box>
      </Box>

      <Collapse in={open}>
        <Box
          sx={{
            color: 'text.default',
            pb: 4,
            paddingLeft: 2
          }}
        >
          {React.Children.map(React.Children.toArray(children), (child, idx) => {
            if (!child) {
              return null
            }
            const uniqueKey = generateUniqueKey()

            return (
              <React.Fragment key={uniqueKey}>{React.cloneElement(child as JSX.Element, {spacing})}</React.Fragment>
            )
          })}
        </Box>
        <RuleItemUserActions />
      </Collapse>
    </Wrapper>
  )
}) as PolymorphicForwardRefComponent<'span' | 'div' | 'button', CollapsibleRuleItemProps & SxProp>

CollapsibleRuleItem.displayName = 'CollapsibleRuleItem'
export default CollapsibleRuleItem
